/**
 * ActionConfirmationCard Component
 *
 * Inline confirm/cancel UI for action confirmation flow.
 * Shows action details, optional reason field for destructive actions.
 * Phase 18: Workflow Automation
 *
 * @module ActionConfirmationCard
 */

'use client';

import { memo, useState, useCallback } from 'react';
import { Box, Text, Flex, Button, Textarea, type BadgeColorPalette } from '@axon/ui';
import { useTranslations } from 'next-intl';
import { LuTriangleAlert, LuShieldCheck, LuShieldAlert } from 'react-icons/lu';

import { useChatbotContext } from '../providers/ChatbotProvider';
import type { ActionConfirmationData } from '../types/chatbot.types';

// ============================================================================
// Props
// ============================================================================

interface ActionConfirmationCardProps {
  confirmation: ActionConfirmationData;
  description: string;
}

// ============================================================================
// Safety Level Config
// ============================================================================

const SAFETY_CONFIG: Record<string, { icon: React.ReactNode; colorPalette: BadgeColorPalette }> = {
  SAFE: {
    icon: <LuShieldCheck size={16} />,
    colorPalette: 'green',
  },
  MODERATE: {
    icon: <LuShieldAlert size={16} />,
    colorPalette: 'yellow',
  },
  DESTRUCTIVE: {
    icon: <LuTriangleAlert size={16} />,
    colorPalette: 'red',
  },
};

// ============================================================================
// Component
// ============================================================================

export const ActionConfirmationCard = memo(function ActionConfirmationCard({
  confirmation,
  description,
}: ActionConfirmationCardProps) {
  const { confirmAction } = useChatbotContext();
  const t = useTranslations('chatbot');
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const safetyLevel = confirmation.safetyLevel || 'MODERATE';
  const config = SAFETY_CONFIG[safetyLevel] || SAFETY_CONFIG.MODERATE;
  const isDestructive = safetyLevel === 'DESTRUCTIVE';

  const handleConfirm = useCallback(() => {
    if (isDestructive && confirmation.requiresReason && !reason.trim()) return;
    setIsSubmitting(true);
    confirmAction(confirmation.confirmationId, true, reason.trim() || undefined);
  }, [
    confirmAction,
    confirmation.confirmationId,
    confirmation.requiresReason,
    isDestructive,
    reason,
  ]);

  const handleCancel = useCallback(() => {
    setIsSubmitting(true);
    confirmAction(confirmation.confirmationId, false);
  }, [confirmAction, confirmation.confirmationId]);

  return (
    <Box
      colorPalette={config.colorPalette}
      marginTop="2"
      padding="3"
      bg="colorPalette.subtle"
      borderRadius="md"
      border="1px solid"
      borderColor="colorPalette.emphasized"
    >
      {/* Header */}
      <Flex align="center" gap="2" marginBottom="2">
        <Box color="colorPalette.fg" flexShrink={0}>
          {config.icon}
        </Box>
        <Text fontSize="sm" fontWeight="semibold" color="colorPalette.fg">
          {description}
        </Text>
      </Flex>

      {/* Action details — skipped when the header description already
          names the entity id (backend-built descriptions usually do),
          which otherwise rendered the same id twice back-to-back. */}
      {confirmation.entityId && !description.includes(confirmation.entityId) && (
        <Flex gap="2" marginBottom="2" flexWrap="wrap">
          <Text fontSize="xs" color="fg.muted">
            {confirmation.entityType}: <strong>{confirmation.entityId}</strong>
          </Text>
        </Flex>
      )}

      {isDestructive && (
        <Text fontSize="xs" color="fg.error" marginBottom="2">
          {t('actions.destructiveWarning')}
        </Text>
      )}

      {/* Reason field for destructive actions */}
      {confirmation.requiresReason && (
        <Box marginBottom="2">
          <Text fontSize="xs" color="fg.muted" marginBottom="1">
            {t('actions.reasonLabel')} {confirmation.requiresReason && '*'}
          </Text>
          <Textarea
            size="sm"
            placeholder={t('actions.reasonPlaceholder')}
            value={reason}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setReason(e.target.value)}
            disabled={isSubmitting}
            rows={2}
          />
        </Box>
      )}

      {/* Action buttons */}
      <Flex gap="2" justify="end" flexWrap="wrap">
        <Button size="sm" variant="outline" onClick={handleCancel} disabled={isSubmitting}>
          {t('actions.cancel')}
        </Button>
        <Button
          size="sm"
          colorPalette={isDestructive ? 'red' : 'blue'}
          onClick={handleConfirm}
          isLoading={isSubmitting}
          disabled={isDestructive && confirmation.requiresReason && !reason.trim()}
        >
          {t('actions.confirm')}
        </Button>
      </Flex>
    </Box>
  );
});

export default ActionConfirmationCard;
